/**
 * Frontend Logging Utility for NBA Chatbot
 * 
 * Integrates with the system_logging module and provides consistent logging
 * across both frontend and backend components.
 */

// Log levels matching the backend system
enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  CRITICAL = 4
}

// Generate daily log file name: frontend_dd_mm_yyyy.log
const getDailyLogFileName = (): string => {
  const today = new Date();
  const day = today.getDate().toString().padStart(2, '0');
  const month = (today.getMonth() + 1).toString().padStart(2, '0');
  const year = today.getFullYear();
  return `frontend_${day}_${month}_${year}.log`;
};

// Get current log level based on environment
const getCurrentLogLevel = (): LogLevel => {
  if (process.env.NODE_ENV === 'production') {
    return LogLevel.INFO;
  }
  return LogLevel.DEBUG;
};

// Format timestamp to match backend format
const formatTimestamp = (): string => {
  return new Date().toISOString().replace('T', ' ').substring(0, 19);
};

// Format log message to match backend format
const formatLogMessage = (level: string, name: string, message: string, meta?: any): string => {
  const timestamp = formatTimestamp();
  const metaStr = meta ? ` | ${JSON.stringify(meta)}` : '';
  return `${timestamp} - ${name} - ${level} - ${message}${metaStr}`;
};

// Console logger with colors matching backend
const consoleLogger = {
  debug: (name: string, message: string, meta?: any) => {
    if (getCurrentLogLevel() <= LogLevel.DEBUG) {
      console.log(`%c${formatLogMessage('DEBUG', name, message, meta)}`, 'color: #6c757d');
    }
  },
  
  info: (name: string, message: string, meta?: any) => {
    if (getCurrentLogLevel() <= LogLevel.INFO) {
      console.log(`%c${formatLogMessage('INFO', name, message, meta)}`, 'color: #007bff');
    }
  },
  
  warn: (name: string, message: string, meta?: any) => {
    if (getCurrentLogLevel() <= LogLevel.WARN) {
      console.warn(`%c${formatLogMessage('WARNING', name, message, meta)}`, 'color: #ffc107');
    }
  },
  
  error: (name: string, message: string, meta?: any) => {
    if (getCurrentLogLevel() <= LogLevel.ERROR) {
      console.error(`%c${formatLogMessage('ERROR', name, message, meta)}`, 'color: #dc3545');
    }
  },
  
  critical: (name: string, message: string, meta?: any) => {
    if (getCurrentLogLevel() <= LogLevel.CRITICAL) {
      console.error(`%c${formatLogMessage('CRITICAL', name, message, meta)}`, 'color: #721c24; background-color: #f8d7da');
    }
  }
};


// Store logs in localStorage for debugging (matches backend file logging concept)
const storeLog = (level: string, name: string, message: string, meta?: any) => {
  try {
    const logData = {
      timestamp: formatTimestamp(),
      level,
      name,
      message,
      meta,
      userAgent: navigator.userAgent,
      url: window.location.href,
      logFileName: getDailyLogFileName()
    };
    
    const logs = JSON.parse(localStorage.getItem('frontend_logs') || '[]');
    logs.push(logData);
    
    // Keep only last 100 logs to prevent localStorage overflow
    if (logs.length > 100) {
      logs.splice(0, logs.length - 100);
    }
    
    localStorage.setItem('frontend_logs', JSON.stringify(logs));
    
  } catch (error) {
    console.error('Failed to store log:', error);
  }
};

// Main logger class that matches backend logger interface
class Logger {
  private name: string;
  
  constructor(name: string) {
    this.name = name;
  }
  
  debug(message: string, meta?: any): void {
    consoleLogger.debug(this.name, message, meta);
    storeLog('DEBUG', this.name, message, meta);
  }
  
  info(message: string, meta?: any): void {
    consoleLogger.info(this.name, message, meta);
    storeLog('INFO', this.name, message, meta);
  }
  
  warning(message: string, meta?: any): void {
    consoleLogger.warn(this.name, message, meta);
    storeLog('WARNING', this.name, message, meta);
  }
  
  warn(message: string, meta?: any): void {
    this.warning(message, meta);
  }
  
  error(message: string, meta?: any): void {
    consoleLogger.error(this.name, message, meta);
    storeLog('ERROR', this.name, message, meta);
  }
  
  critical(message: string, meta?: any): void {
    consoleLogger.critical(this.name, message, meta);
    storeLog('CRITICAL', this.name, message, meta);
  }
}

// Factory function to get logger (matches backend get_logger function)
export const getLogger = (name: string): Logger => {
  return new Logger(name);
};

// Convenience functions for common logging scenarios
export const logInfo = (name: string, message: string, meta?: any) => {
  getLogger(name).info(message, meta);
};

export const logError = (name: string, message: string, error?: any) => {
  getLogger(name).error(message, { 
    error: error?.message || error, 
    stack: error?.stack,
    type: error?.constructor?.name 
  });
};

export const logWarn = (name: string, message: string, meta?: any) => {
  getLogger(name).warning(message, meta);
};

export const logDebug = (name: string, message: string, meta?: any) => {
  getLogger(name).debug(message, meta);
};

// Utility functions for debugging
export const getStoredLogs = (): any[] => {
  try {
    return JSON.parse(localStorage.getItem('frontend_logs') || '[]');
  } catch {
    return [];
  }
};

export const clearStoredLogs = (): void => {
  localStorage.removeItem('frontend_logs');
};

export const exportLogs = (): string => {
  const logs = getStoredLogs();
  return JSON.stringify(logs, null, 2);
};

// Initialize frontend logging system
const initializeFrontendLogging = () => {
  const logger = getLogger('system_logging');
  logger.info('Frontend logging system initialized', {
    environment: process.env.NODE_ENV,
    logLevel: LogLevel[getCurrentLogLevel()],
    logFileName: getDailyLogFileName()
  });
};

// Auto-initialize when module is loaded
if (typeof window !== 'undefined') {
  initializeFrontendLogging();
}

const loggerModule = {
  getLogger,
  logInfo,
  logError,
  logWarn,
  logDebug,
  getStoredLogs,
  clearStoredLogs,
  exportLogs
};

export default loggerModule; 